import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Row, Col, Select, Table, Typography, Tooltip } from 'antd';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import { setRolePreset } from '../../../utils/role';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_KPI = {
    orderTotal: 1240,       // 订单总量
    gmvCny: 35600,          // GMV(¥) = 营收总额 + 退款金额
    gmvUsd: 4200,           // GMV($)
    revenueCny: 33200,      // 营收总额(¥)
    revenueUsd: 3950,       // 营收总额($)
    refundOrders: 48,       // 退款订单数
    refundAmount: 2400,     // 退款金额(¥)
    refundAmountUsd: 250,   // 退款金额($)
};

// TODO: 联调时替换为接口数据 —— 订单/营收（月粒度，近12个月）
const MOCK_ORDER_MONTHLY = [
    { month: '2025-09', orderCnt: 90, refundCnt: 3, revenueCny: 8500, revenueUsd: 1000 },
    { month: '2025-10', orderCnt: 105, refundCnt: 4, revenueCny: 9700, revenueUsd: 1150 },
    { month: '2025-11', orderCnt: 98, refundCnt: 3, revenueCny: 9100, revenueUsd: 1080 },
    { month: '2025-12', orderCnt: 112, refundCnt: 4, revenueCny: 10200, revenueUsd: 1210 },
    { month: '2026-01', orderCnt: 118, refundCnt: 5, revenueCny: 10800, revenueUsd: 1280 },
    { month: '2026-02', orderCnt: 104, refundCnt: 4, revenueCny: 9600, revenueUsd: 1140 },
    { month: '2026-03', orderCnt: 96, refundCnt: 3, revenueCny: 8900, revenueUsd: 1050 },
    { month: '2026-04', orderCnt: 110, refundCnt: 4, revenueCny: 10100, revenueUsd: 1200 },
    { month: '2026-05', orderCnt: 122, refundCnt: 5, revenueCny: 11200, revenueUsd: 1330 },
    { month: '2026-06', orderCnt: 128, refundCnt: 6, revenueCny: 11800, revenueUsd: 1400 },
    { month: '2026-07', orderCnt: 135, refundCnt: 6, revenueCny: 12400, revenueUsd: 1470 },
    { month: '2026-08', orderCnt: 140, refundCnt: 7, revenueCny: 12900, revenueUsd: 1530 },
];

// TODO: 联调时替换为接口数据 —— 订单/营收（日粒度，近30天）
const MOCK_ORDER_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const orderSeed = [6, 5, 7, 8, 6, 9, 7, 10, 8, 6, 11, 8, 7, 9, 8, 10, 9, 7, 11, 12, 10, 8, 12, 13, 11, 9, 12, 14, 11, 10];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const order = orderSeed[29 - i];
        arr.push({
            date: dateStr,
            orderCnt: order,
            refundCnt: Math.round(order * 0.04),
            revenueCny: order * 88,
            revenueUsd: order * 10.5,
        });
    }
    return arr;
})();

const timeRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: '3m', label: '近3个月' },
    { value: '6m', label: '近6个月' },
    { value: '1y', label: '近一年' },
];

// 根据时间范围返回订单/营收趋势数据（日粒度：近7天/近30天/本月；月粒度：近3月/6月/一年）
const getOrderTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_ORDER_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            orderCnt: daily.map((d) => d.orderCnt),
            refundCnt: daily.map((d) => d.refundCnt),
            revenueCny: daily.map((d) => d.revenueCny),
            revenueUsd: daily.map((d) => d.revenueUsd),
            refundAmountCny: daily.map((d) => d.refundCnt * 50),
            refundAmountUsd: daily.map((d) => d.refundCnt * 5),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_ORDER_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        orderCnt: monthly.map((m) => m.orderCnt),
        refundCnt: monthly.map((m) => m.refundCnt),
        revenueCny: monthly.map((m) => m.revenueCny),
        revenueUsd: monthly.map((m) => m.revenueUsd),
        refundAmountCny: monthly.map((m) => m.refundCnt * 50),
        refundAmountUsd: monthly.map((m) => m.refundCnt * 5),
    };
};

// TODO: 联调时替换为接口数据 —— 卡商排名（按卡商聚合）
const MOCK_VENDOR_RANK = [
    { key: '1', vendorName: '移远通信(瀚晖)', vendorId: 'VENDOR_HH_001', orderCount: 520, revenueCny: 15600, revenueUsd: 1860, deviceCount: 2100 },
    { key: '2', vendorName: '麦联宝', vendorId: 'VENDOR_MLB_002', orderCount: 340, revenueCny: 10200, revenueUsd: 1210, deviceCount: 1400 },
    { key: '3', vendorName: '桔子智联', vendorId: 'VENDOR_JZ_003', orderCount: 210, revenueCny: 6300, revenueUsd: 750, deviceCount: 860 },
    { key: '4', vendorName: '万物互联电信', vendorId: 'VENDOR_WW_004', orderCount: 120, revenueCny: 3600, revenueUsd: 430, deviceCount: 480 },
    { key: '5', vendorName: '未识别卡商', vendorId: '--', orderCount: 50, revenueCny: 1500, revenueUsd: 180, deviceCount: 160 },
];

// TODO: 联调时替换为接口数据 —— 流量套餐排名（按套餐聚合）
const MOCK_PACKAGE_RANK = [
    { key: '1', name: '2年优惠套餐', id: 'FLOW_PKG_2Y_157', orderCount: 460, revenueCny: 13700, revenueUsd: 1630, deviceCount: 1900 },
    { key: '2', name: '1年流量套餐', id: 'FLOW_PKG_1Y_88', orderCount: 380, revenueCny: 10600, revenueUsd: 1260, deviceCount: 1500 },
    { key: '3', name: '季度流量套餐', id: 'FLOW_PKG_Q_30', orderCount: 220, revenueCny: 6400, revenueUsd: 760, deviceCount: 880 },
    { key: '4', name: '月度流量套餐', id: 'FLOW_PKG_M_10', orderCount: 130, revenueCny: 3400, revenueUsd: 410, deviceCount: 520 },
    { key: '5', name: '日租流量套餐', id: 'FLOW_PKG_D_1', orderCount: 50, revenueCny: 1300, revenueUsd: 160, deviceCount: 200 },
];

// TODO: 联调时替换为接口数据 —— 首充/复充
const MOCK_RECHARGE = {
    firstRate: 58.0,        // 首充率 %
    firstCnt: 720,          // 首充订单数
    firstCny: 19200,        // 首充金额(¥)
    firstUsd: 2280,         // 首充金额($)
    repurchaseRate: 42.0,   // 复充率 %
    repurchaseCnt: 520,     // 复充订单数
    repurchaseCny: 14000,   // 复充金额(¥)
    repurchaseUsd: 1670,    // 复充金额($)
};

// TODO: 联调时替换为接口数据 —— 畅销流量套餐
const MOCK_HOT_PACKAGES = [
    { key: '1', id: 'FLOW_PKG_2Y_157', name: '2年优惠套餐', sales: 460, amountCny: 13700, amountUsd: 1630 },
    { key: '2', id: 'FLOW_PKG_1Y_88', name: '1年流量套餐', sales: 380, amountCny: 10600, amountUsd: 1260 },
    { key: '3', id: 'FLOW_PKG_Q_30', name: '季度流量套餐', sales: 220, amountCny: 6400, amountUsd: 760 },
    { key: '4', id: 'FLOW_PKG_M_10', name: '月度流量套餐', sales: 130, amountCny: 3400, amountUsd: 410 },
    { key: '5', id: 'FLOW_PKG_D_1', name: '日租流量套餐', sales: 50, amountCny: 1300, amountUsd: 160 },
];

const RANK_BADGE = {
    1: { text: 'Top1', color: '#f59e0b' },
    2: { text: 'Top2', color: '#9ca3af' },
    3: { text: 'Top3', color: '#d97706' },
};

// 排名方式选项
const rankOptions = [
    { value: 'orderCount', label: '按有效单量' },
    { value: 'deviceCount', label: '按设备规模' },
    { value: 'revenueCny', label: '按人民币营收' },
    { value: 'revenueUsd', label: '按美元营收' },
];

// 退款口径选项（首充复充 / 畅销套餐共用）
const refundModeOptions = [
    { value: 'exclude', label: '剔除退款' },
    { value: 'include', label: '包含退款' },
];

// 表头不换行
const tableHeaderNowrap = {
    header: {
        cell: (props) => <th {...props} style={{ ...props.style, whiteSpace: 'nowrap' }} />,
    },
};

const FourGRevenue = () => {
    // 企业视角：本企业导入流量卡数据
    setRolePreset('enterprise', 'app');

    const [timeRange, setTimeRange] = useState('7d');
    const [rechargeRefundMode, setRechargeRefundMode] = useState('exclude'); // 首充复充退款口径
    const [hotPackageRefundMode, setHotPackageRefundMode] = useState('exclude'); // 畅销套餐退款口径
    const [vendorSortKey, setVendorSortKey] = useState('orderCount'); // 卡商排名
    const [pkgSortKey, setPkgSortKey] = useState('orderCount'); // 流量套餐排名

    const trend1Ref = useRef(null);
    const trend2Ref = useRef(null);
    const trend1Instance = useRef(null);
    const trend2Instance = useRef(null);
    const rechargeRef1 = useRef(null);
    const rechargeRef2 = useRef(null);
    const rechargeInstance1 = useRef(null);
    const rechargeInstance2 = useRef(null);

    // 图表1：订单量趋势（订单量 + 退款订单，依赖时间范围）
    useEffect(() => {
        const { labels, orderCnt, refundCnt } = getOrderTrendData(timeRange);
        if (!trend1Ref.current) return;
        if (!trend1Instance.current) {
            trend1Instance.current = echarts.init(trend1Ref.current);
            trend1Instance.current.setOption({
                legend: { data: ['订单量', '退款订单'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 60, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '订单数', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '订单量', type: 'bar', data: orderCnt, barWidth: 24, itemStyle: { color: '#5470c6' } },
                    { name: '退款订单', type: 'line', data: refundCnt, smooth: true, lineStyle: { color: '#ee6666', width: 2.5 }, itemStyle: { color: '#ee6666' }, symbol: 'circle', symbolSize: 6 },
                ],
            });
        } else {
            trend1Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '订单量', data: orderCnt },
                    { name: '退款订单', data: refundCnt },
                ],
            });
        }
    }, [timeRange]);

    // 图表2：营收趋势（按币种双柱堆叠：有效营收 + 退款金额，依赖时间范围）
    useEffect(() => {
        const d = getOrderTrendData(timeRange);
        if (!trend2Ref.current) return;
        const tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const idx = params[0].dataIndex;
                const label = params[0].axisValue;
                return [
                    label,
                    `有效营收(¥)：¥${d.revenueCny[idx].toLocaleString('zh-CN')}`,
                    `退款金额(¥)：¥${d.refundAmountCny[idx].toLocaleString('zh-CN')}`,
                    `有效营收($)：$${d.revenueUsd[idx].toLocaleString('zh-CN')}`,
                    `退款金额($)：$${d.refundAmountUsd[idx].toLocaleString('zh-CN')}`,
                ].join('<br/>');
            },
        };
        if (!trend2Instance.current) {
            trend2Instance.current = echarts.init(trend2Ref.current);
            trend2Instance.current.setOption({
                legend: { data: ['有效营收(¥)', '退款金额(¥)', '有效营收($)', '退款金额($)'], top: 10, selectedMode: true },
                tooltip,
                grid: { top: 60, left: 70, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: d.labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '金额', min: 0, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '有效营收(¥)', type: 'bar', stack: 'cny', data: d.revenueCny, barWidth: 16, itemStyle: { color: '#91cc75' } },
                    { name: '退款金额(¥)', type: 'bar', stack: 'cny', data: d.refundAmountCny, itemStyle: { color: '#ee6666' } },
                    { name: '有效营收($)', type: 'bar', stack: 'usd', data: d.revenueUsd, barWidth: 16, itemStyle: { color: '#5470c6' } },
                    { name: '退款金额($)', type: 'bar', stack: 'usd', data: d.refundAmountUsd, itemStyle: { color: '#fc8452' } },
                ],
            });
        } else {
            trend2Instance.current.setOption({
                tooltip,
                xAxis: { data: d.labels },
                series: [
                    { name: '有效营收(¥)', data: d.revenueCny },
                    { name: '退款金额(¥)', data: d.refundAmountCny },
                    { name: '有效营收($)', data: d.revenueUsd },
                    { name: '退款金额($)', data: d.refundAmountUsd },
                ],
            });
        }
    }, [timeRange]);

    // 首充复充环形图（首充率 / 复充率）
    useEffect(() => {
        const renderRing = (ref, instance, rate, color, title) => {
            if (!ref.current) return;
            instance.current = echarts.init(ref.current);
            instance.current.setOption({
                series: [{
                    type: 'pie',
                    radius: ['62%', '82%'],
                    avoidLabelOverlap: false,
                    label: { show: false },
                    data: [
                        { value: rate, name: '占比' },
                        { value: Math.round((100 - rate) * 10) / 10, name: '其他' },
                    ],
                    color: [color, '#f0f0f0'],
                    itemStyle: { borderColor: '#fff', borderWidth: 2 },
                }],
                graphic: [
                    { type: 'text', left: 'center', top: '34%', style: { text: `${rate}%`, textAlign: 'center', fontSize: 20, fontWeight: 'bold', fill: color } },
                    { type: 'text', left: 'center', top: '56%', style: { text: title, textAlign: 'center', fontSize: 12, fill: '#606266' } },
                ],
            });
        };
        renderRing(rechargeRef1, rechargeInstance1, MOCK_RECHARGE.firstRate, '#1890ff', '首充率');
        renderRing(rechargeRef2, rechargeInstance2, MOCK_RECHARGE.repurchaseRate, '#52c41a', '复充率');
    }, []);

    useEffect(() => {
        const handleResize = () => {
            trend1Instance.current?.resize();
            trend2Instance.current?.resize();
            rechargeInstance1.current?.resize();
            rechargeInstance2.current?.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const kpiCards = [
        { title: '订单总量', value: MOCK_KPI.orderTotal, suffix: '笔', color: '#52c41a', tip: '不区分订单状态的4G订单总数' },
        { title: 'GMV', cny: MOCK_KPI.gmvCny, usd: MOCK_KPI.gmvUsd, color: '#1890ff', tip: '总交易额（有效营收 + 退款金额，人民币 / 美元）', multiCurrency: true },
        { title: '营收总额', cny: MOCK_KPI.revenueCny, usd: MOCK_KPI.revenueUsd, color: '#722ed1', tip: '订单支付金额总和 - 退款单退款总额', multiCurrency: true },
        { title: '退款订单数', value: MOCK_KPI.refundOrders, suffix: '笔', color: '#fa8c16', tip: '已部分退款或全额退款的订单数' },
        { title: '退款金额', cny: MOCK_KPI.refundAmount, usd: MOCK_KPI.refundAmountUsd, color: '#eb2f96', tip: '退款单总额', multiCurrency: true },
    ];

    const vendorColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => RANK_BADGE[v]
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: RANK_BADGE[v].color, color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '卡商', key: 'vendor',
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.vendorName}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.vendorId}</Text>
            </div>),
        },
        { title: '有效订单', dataIndex: 'orderCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '设备规模', dataIndex: 'deviceCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        {
            title: '有效营收', key: 'revenue', align: 'right',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
    ];

    const pkgColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => RANK_BADGE[v]
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: RANK_BADGE[v].color, color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '套餐', key: 'pkg',
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        { title: '有效订单', dataIndex: 'orderCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        { title: '设备规模', dataIndex: 'deviceCount', align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        {
            title: '有效营收', key: 'revenue', align: 'right',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.revenueCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.revenueUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
    ];

    const vendorData = [...MOCK_VENDOR_RANK].sort((a, b) => b[vendorSortKey] - a[vendorSortKey]).map((r, i) => ({ ...r, rank: i + 1 }));
    const pkgData = [...MOCK_PACKAGE_RANK].sort((a, b) => b[pkgSortKey] - a[pkgSortKey]).map((r, i) => ({ ...r, rank: i + 1 }));

    const hotPackageData = [...MOCK_HOT_PACKAGES]
        .sort((a, b) => b.sales - a.sales)
        .map((r, i) => ({ ...r, rank: i + 1 }));

    const hotPackageColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 70, align: 'center',
            render: (v) => v <= 3
                ? <span style={{ display: 'inline-block', minWidth: 40, padding: '2px 8px', borderRadius: 10, background: ['#f59e0b', '#9ca3af', '#d97706'][v - 1], color: '#fff', fontWeight: 600, fontSize: 12 }}>Top{v}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '套餐名称', key: 'name', width: 200,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        { title: '销量', dataIndex: 'sales', width: 90, align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
        {
            title: '销售金额', key: 'amount', width: 150, align: 'right',
            render: (_, r) => (
                <div>
                    <div style={{ whiteSpace: 'nowrap' }}>¥{r.amountCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                    <div style={{ whiteSpace: 'nowrap' }}>${r.amountUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                </div>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><Title level={4}>4G流量收益</Title></div>
            <Alert
                type="warning"
                showIcon
                icon={<EyeOutlined />}
                message="本看板展示贵司导入流量卡产生的 4G 流量订单经营情况，涵盖订单量、营收、退款、首充复充、套餐畅销及卡商排名等，统计范围为本企业导入的流量卡。"
                style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* ═══════════ 核心指标 ═══════════ */}
            <Card title="核心指标" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Row gutter={[16, 16]}>
                {kpiCards.map((c) => (
                    <Col flex="1 1 0" key={c.title}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', minHeight: 100 }}>
                            <div style={{ fontSize: 12, color: '#606266', marginBottom: 8 }}>
                                {c.title}
                                <Tooltip title={c.tip}><InfoCircleOutlined style={{ color: '#999', fontSize: 12, marginLeft: 4, cursor: 'help' }} /></Tooltip>
                            </div>
                            {c.multiCurrency ? (
                                <div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap' }}>¥{c.cny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                    <div style={{ color: c.color, fontWeight: 'bold', fontSize: 18, whiteSpace: 'nowrap' }}>${c.usd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            ) : (
                                <div style={{ color: c.color, fontWeight: 'bold', fontSize: c.money ? 20 : 22, whiteSpace: 'nowrap', lineHeight: '32px' }}>
                                    {c.money ? `¥${c.value.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}` : `${c.value}${c.suffix}`}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
                </Row>
            </Card>

            {/* ═══════════ 订单量趋势 / 首充复充 ═══════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Card
                        title="订单量趋势"
                        extra={(
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                                <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                            </span>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <div ref={trend1Ref} style={{ width: '100%', height: 300 }} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        title={(
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span>首充复充</span>
                                <Select value={rechargeRefundMode} onChange={setRechargeRefundMode} options={refundModeOptions} style={{ width: 120 }} />
                            </div>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div ref={rechargeRef1} style={{ width: '100%', height: 150 }} />
                                <div style={{ textAlign: 'center', marginTop: 8 }}>
                                    <div style={{ color: '#606266', fontSize: 12 }}>首充订单数</div>
                                    <div style={{ fontWeight: 'bold', fontSize: 18 }}>{MOCK_RECHARGE.firstCnt.toLocaleString()}</div>
                                    <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>¥{MOCK_RECHARGE.firstCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} / ${MOCK_RECHARGE.firstUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div ref={rechargeRef2} style={{ width: '100%', height: 150 }} />
                                <div style={{ textAlign: 'center', marginTop: 8 }}>
                                    <div style={{ color: '#606266', fontSize: 12 }}>复充订单数</div>
                                    <div style={{ fontWeight: 'bold', fontSize: 18 }}>{MOCK_RECHARGE.repurchaseCnt.toLocaleString()}</div>
                                    <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>¥{MOCK_RECHARGE.repurchaseCny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} / ${MOCK_RECHARGE.repurchaseUsd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ 营收趋势 / 套餐畅销 ═══════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Card
                        title="营收趋势"
                        extra={(
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                                <Select value={timeRange} onChange={setTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                            </span>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <div ref={trend2Ref} style={{ width: '100%', height: 300 }} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        title={(
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span>套餐畅销</span>
                                <Select value={hotPackageRefundMode} onChange={setHotPackageRefundMode} options={refundModeOptions} style={{ width: 120 }} />
                            </div>
                        )}
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    >
                        <Table columns={hotPackageColumns} dataSource={hotPackageData} rowKey="key" pagination={false} size="small" style={{ whiteSpace: 'nowrap' }} />
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ 卡商排名 / 流量套餐排名 ═══════════ */}
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>卡商排名</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>排名方式</span>
                            <Select value={vendorSortKey} onChange={setVendorSortKey} options={rankOptions} style={{ width: 140 }} />
                        </div>
                        <Table columns={vendorColumns} dataSource={vendorData} rowKey="key" pagination={false} components={tableHeaderNowrap} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>流量套餐排名</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>排名方式</span>
                            <Select value={pkgSortKey} onChange={setPkgSortKey} options={rankOptions} style={{ width: 140 }} />
                        </div>
                        <Table columns={pkgColumns} dataSource={pkgData} rowKey="key" pagination={false} components={tableHeaderNowrap} />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FourGRevenue;
